"use client";

import type { ReactNode } from "react";
import { Badge, Button, ScrollArea } from "@wtc/ui";
import { ChevronDown, ChevronRight, Loader2, Star } from "lucide-react";

import type { EmailTemplateLike } from "../types";
import type { FolderWithChildren } from "../folder-tree";
import { FolderNode } from "./FolderNode";
import { TemplateItem } from "./TemplateItem";

export interface TemplateBrowserProps<T extends EmailTemplateLike> {
  isLoading: boolean;
  isDebouncing: boolean;
  rootTemplates: T[];
  folderTree: FolderWithChildren<T>[];
  favoriteTemplates: T[];
  favoritesExpanded: boolean;
  onFavoritesExpandedChange: (open: boolean) => void;
  allTemplateCount: number;
  isSearching: boolean;
  searchTerm: string;
  selectedTag: string;
  selectedTemplateId?: string;
  expandedFolders: Set<string>;
  onToggleFolder: (folderId: string) => void;
  onPreviewTemplate: (template: T) => void;
  onApplyTemplate: (template: T) => void;
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  wrapRow?: (template: T, row: ReactNode) => ReactNode;
}

export function TemplateBrowser<T extends EmailTemplateLike>({
  isLoading,
  isDebouncing,
  rootTemplates,
  folderTree,
  favoriteTemplates,
  favoritesExpanded,
  onFavoritesExpandedChange,
  allTemplateCount,
  isSearching,
  searchTerm,
  selectedTag,
  selectedTemplateId,
  expandedFolders,
  onToggleFolder,
  onPreviewTemplate,
  onApplyTemplate,
  isFavorite,
  onToggleFavorite,
  wrapRow,
}: TemplateBrowserProps<T>) {
  return (
    <ScrollArea className="border rounded-md">
      <div className="p-3 space-y-1">
        {isLoading ? (
          <div className="text-center text-muted-foreground py-6 text-sm">Loading templates...</div>
        ) : (
          <>
            {isDebouncing && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching...
              </div>
            )}

            {rootTemplates.length > 0 && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                  Uncategorized ({rootTemplates.length})
                </div>
                {rootTemplates.map((template) => (
                  <TemplateItem
                    key={template.id}
                    template={template}
                    level={0}
                    selected={selectedTemplateId === template.id}
                    onPreview={onPreviewTemplate}
                    onApply={onApplyTemplate}
                    isFavorite={isFavorite(template.id)}
                    onToggleFavorite={onToggleFavorite}
                    wrapRow={wrapRow}
                  />
                ))}
              </div>
            )}

            {folderTree.map((folder) => (
              <FolderNode
                key={folder.id}
                folder={folder}
                level={0}
                onPreviewTemplate={onPreviewTemplate}
                onApplyTemplate={onApplyTemplate}
                selectedTemplateId={selectedTemplateId}
                expandedFolders={expandedFolders}
                onToggleFolder={onToggleFolder}
                isFavorite={isFavorite}
                onToggleFavorite={onToggleFavorite}
                wrapRow={wrapRow}
              />
            ))}

            {favoriteTemplates.length > 0 && (
              <div className="space-y-0.5 pt-3 mt-3 border-t border-border/80">
                <Button
                  type="button"
                  variant="ghost"
                  aria-expanded={favoritesExpanded}
                  aria-controls="template-favorites"
                  onClick={() => onFavoritesExpandedChange(!favoritesExpanded)}
                  className="w-full justify-start text-left font-normal hover:bg-muted/50"
                  style={{ paddingLeft: "8px" }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {favoritesExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <Star className="h-3 w-3 text-amber-500 fill-amber-400 shrink-0" />
                    <span className="flex-1 text-xs font-medium text-left">Favorites</span>
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 shrink-0">
                      {favoriteTemplates.length}
                    </Badge>
                  </div>
                </Button>
                {favoritesExpanded && (
                  <div id="template-favorites" className="space-y-0.5">
                    {favoriteTemplates.map((template) => (
                      <TemplateItem
                        key={`fav-${template.id}`}
                        template={template}
                        level={0}
                        selected={selectedTemplateId === template.id}
                        onPreview={onPreviewTemplate}
                        onApply={onApplyTemplate}
                        isFavorite={isFavorite(template.id)}
                        onToggleFavorite={onToggleFavorite}
                        wrapRow={wrapRow}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {allTemplateCount === 0 && (
              <div className="text-center text-muted-foreground py-6 text-sm">
                {isSearching
                  ? `No templates found matching "${searchTerm}"`
                  : selectedTag
                    ? `No templates found with tag "${selectedTag}".`
                    : "No email templates found."}
              </div>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
}
